import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  FileText, Download, Filter, Users, FolderTree, ChevronDown, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReportsDashboard() {
  const navigate = useNavigate();
  const { axiosAuth, token, user: authUser } = useAuth();
  const [reports, setReports] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [filterOrg, setFilterOrg] = useState('');
  const [filterVertical, setFilterVertical] = useState('');
  const [filterTeam, setFilterTeam] = useState('');

  const isSuperAdmin = authUser?.role === 'super_admin';
  const isManager = authUser?.role === 'manager';

  const fetchFilters = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [oRes, vRes, tRes] = await Promise.all([
        isSuperAdmin ? api.get('/admin/orgs').catch(() => ({ data: { orgs: [] } })) : Promise.resolve({ data: { orgs: [] } }),
        api.get('/admin/verticals').catch(() => ({ data: { verticals: [] } })),
        api.get('/admin/teams').catch(() => ({ data: { teams: [] } })),
      ]);
      setOrgs(oRes.data.orgs || []);
      setVerticals(vRes.data.verticals || []);
      setTeams(tRes.data.teams || []);
    } catch { /* silent */ }
  }, [axiosAuth, isSuperAdmin]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const params = new URLSearchParams();
      if (filterOrg) params.set('org_id', filterOrg);
      if (filterVertical) params.set('vertical_id', filterVertical);
      if (filterTeam) params.set('team_id', filterTeam);
      const res = await api.get(`/admin/reports/simulations?${params}`);
      setReports(res.data.reports || []);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Access denied'); navigate('/'); }
      else toast.error('Failed to load reports');
    } finally { setLoading(false); }
  }, [axiosAuth, filterOrg, filterVertical, filterTeam, navigate]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Reset downstream filters when parent changes
  useEffect(() => { setFilterVertical(''); setFilterTeam(''); }, [filterOrg]);
  useEffect(() => { setFilterTeam(''); }, [filterVertical]);

  // Cascade filter options
  const filteredVerticals = filterOrg
    ? verticals.filter(v => v.org_id === filterOrg)
    : verticals;
  const filteredTeams = filterVertical
    ? teams.filter(t => t.vertical_id === filterVertical)
    : filterOrg
      ? teams.filter(t => {
          const vIds = verticals.filter(v => v.org_id === filterOrg).map(v => v.id);
          return vIds.includes(t.vertical_id);
        })
      : teams;

  const downloadSinglePdf = async (simId, title) => {
    try {
      const res = await fetch(`${API}/api/simulations/${simId}/report?report_type=summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${title.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const downloadBulkPdf = async () => {
    const simIds = reports.map(r => r.sim_id);
    if (!simIds.length) return toast.error('No reports to export');
    if (simIds.length > 50) return toast.error('Maximum 50 reports per bulk export. Apply filters to narrow down.');
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/reports/bulk-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sim_ids: simIds }),
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `simulify_bulk_${simIds.length}_reports.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloading ${simIds.length} reports`);
    } catch {
      toast.error('Failed to generate bulk PDF');
    } finally { setBulkLoading(false); }
  };

  const scoreColor = (s) => s >= 8 ? 'text-emerald-500' : s >= 6 ? 'text-amber-500' : 'text-red-500';
  const scoreBg = (s) => s >= 8 ? 'bg-emerald-500/10' : s >= 6 ? 'bg-amber-500/10' : 'bg-red-500/10';

  return (
    <div className="min-h-screen bg-background" data-testid="reports-dashboard-page">
      <Navbar showBack backTo="/admin" backLabel="Reports" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Simulation Reports</h1>
            <p className="font-body text-sm text-muted-foreground mt-1">
              {authUser?.role === 'manager' ? 'Reports for your team' : 'Reports across your organization'}
            </p>
          </div>
          <Button data-testid="bulk-pdf-btn" onClick={downloadBulkPdf} disabled={bulkLoading || !reports.length}
            className="bg-brand text-[#333] gap-2 h-9 px-4 text-xs font-medium rounded-lg hover:opacity-90 disabled:opacity-50">
            {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {bulkLoading ? 'Generating...' : `Export All (${reports.length}) as PDF`}
          </Button>
        </div>

        {/* Filters */}
        <div className="heritage-card p-4 mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center" data-testid="report-filters">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Filter className="w-4 h-4" />
            <span className="font-body text-xs font-medium">Filters:</span>
          </div>

          <div className="flex flex-wrap gap-3">
            {isSuperAdmin && (
            <Select value={filterOrg || '_all'} onValueChange={v => setFilterOrg(v === '_all' ? '' : v)}>
              <SelectTrigger data-testid="filter-org" className="w-48 border border-border h-8 font-body text-xs bg-surface">
                <Filter className="w-3 h-3 mr-1 text-muted-foreground" />
                <SelectValue placeholder="All Organizations" />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                <SelectItem value="_all" className="font-body text-xs">All Organizations</SelectItem>
                {orgs.map(o => (
                  <SelectItem key={o.id} value={o.id} className="font-body text-xs">{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}

            {!isManager && (
            <Select value={filterVertical || '_all'} onValueChange={v => setFilterVertical(v === '_all' ? '' : v)}>
              <SelectTrigger data-testid="filter-vertical" className="w-48 border border-border h-8 font-body text-xs bg-surface">
                <FolderTree className="w-3 h-3 mr-1 text-muted-foreground" />
                <SelectValue placeholder="All Verticals" />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                <SelectItem value="_all" className="font-body text-xs">All Verticals</SelectItem>
                {filteredVerticals.map(v => (
                  <SelectItem key={v.id} value={v.id} className="font-body text-xs">{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}

            {!isManager && (
            <Select value={filterTeam || '_all'} onValueChange={v => setFilterTeam(v === '_all' ? '' : v)}>
              <SelectTrigger data-testid="filter-team" className="w-48 border border-border h-8 font-body text-xs bg-surface">
                <Users className="w-3 h-3 mr-1 text-muted-foreground" />
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent className="border border-border shadow-modal bg-surface">
                <SelectItem value="_all" className="font-body text-xs">All Teams</SelectItem>
                {filteredTeams.map(t => (
                  <SelectItem key={t.id} value={t.id} className="font-body text-xs">{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}

            {(filterOrg || filterVertical || filterTeam) && (
              <Button variant="ghost" onClick={() => { setFilterOrg(''); setFilterVertical(''); setFilterTeam(''); }}
                className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground">
                Clear filters
              </Button>
            )}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="heritage-card p-12 text-center">
            <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="font-body text-sm text-muted-foreground">Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="heritage-card p-12 text-center">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-heading text-sm font-bold mb-1">No Reports Found</p>
            <p className="font-body text-xs text-muted-foreground">No completed simulations match the current filters</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="reports-list">
            {/* Header row */}
            <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              <div className="col-span-2">User</div>
              <div className="col-span-2">Scenario</div>
              <div className="col-span-1">Org</div>
              <div className="col-span-1">Vertical</div>
              <div className="col-span-1">Team</div>
              <div className="col-span-1 text-center">Score</div>
              <div className="col-span-2">Completed</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {reports.map((r, i) => (
              <div key={`${r.sim_id}-${i}`} data-testid={`report-row-${i}`}
                className="heritage-card p-3 sm:p-4 sm:grid sm:grid-cols-12 sm:gap-2 sm:items-center flex flex-col gap-2 hover:border-brand/20 transition-colors">
                {/* User */}
                <div className="col-span-2">
                  <p className="font-heading text-xs font-bold truncate">{r.user_name}</p>
                  <p className="font-body text-[10px] text-muted-foreground truncate">{r.user_email}</p>
                </div>
                {/* Scenario */}
                <div className="col-span-2">
                  <p className="font-body text-xs truncate">{r.scenario_title}</p>
                  <Badge className="bg-muted text-muted-foreground border-none text-[9px] px-1 py-0 mt-0.5">{r.difficulty}</Badge>
                </div>
                {/* Org */}
                <div className="col-span-1">
                  {r.org ? (
                    <Badge className="bg-accent/10 text-accent border-none text-[9px] px-1.5 py-0 truncate max-w-full">{r.org}</Badge>
                  ) : (
                    <span className="font-body text-[10px] text-muted-foreground">—</span>
                  )}
                </div>
                {/* Vertical */}
                <div className="col-span-1">
                  {r.vertical ? (
                    <Badge className="bg-info/10 text-info border-none text-[9px] px-1.5 py-0 truncate max-w-full">{r.vertical}</Badge>
                  ) : (
                    <span className="font-body text-[10px] text-muted-foreground">—</span>
                  )}
                </div>
                {/* Team */}
                <div className="col-span-1">
                  {r.team ? (
                    <Badge className="bg-success/10 text-success border-none text-[9px] px-1.5 py-0 truncate max-w-full">{r.team}</Badge>
                  ) : (
                    <span className="font-body text-[10px] text-muted-foreground">—</span>
                  )}
                </div>
                {/* Score */}
                <div className="col-span-1 text-center">
                  <span className={`inline-flex items-center justify-center w-10 h-6 rounded-md text-xs font-bold ${scoreBg(r.score)} ${scoreColor(r.score)}`}>
                    {r.score}/10
                  </span>
                </div>
                {/* Date */}
                <div className="col-span-2">
                  <p className="font-body text-[10px] text-muted-foreground">
                    {r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                  </p>
                </div>
                {/* Actions */}
                <div className="col-span-2 flex justify-end gap-1">
                  <button data-testid={`view-report-${i}`}
                    onClick={() => navigate(`/report/${r.sim_id}`)}
                    className="w-7 h-7 border border-border rounded-md flex items-center justify-center hover:bg-muted transition-colors" title="View report">
                    <FileText className="w-3 h-3 text-muted-foreground" />
                  </button>
                  <button data-testid={`download-pdf-${i}`}
                    onClick={() => downloadSinglePdf(r.sim_id, r.scenario_title)}
                    className="w-7 h-7 border border-brand/30 rounded-md flex items-center justify-center hover:bg-brand/10 transition-colors" title="Download PDF">
                    <Download className="w-3 h-3 text-brand" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
